import React, { useState, useEffect, Suspense, useTransition } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import ThemeProvider, { BrandLogo } from './providers/ThemeProvider';
import { INITIAL_LEADS, INITIAL_TEMPLATES } from './constants/initialData';
import { useIsMobile } from './hooks/useIsMobile';
import { prepareForStorage, getStatusForStorage, getFullName, getCompanyName } from './utils/fieldMappings';
import EpisodeRoute from './routes/EpisodeRoute';
import { Bell, User, Settings, LayoutDashboard, Users, MessageSquare, Calendar, Send, Home, LogOut, X, Loader, Star, Paperclip, FileX, ChevronLeft, Mic, Target, ChevronUp, Mail, Activity, Clock, CheckCircle, MousePointer, AlertCircle, UserMinus, AlertTriangle, Play, Zap, Search } from 'lucide-react';

// Firebase Imports
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, collection, doc, onSnapshot, addDoc, updateDoc, setDoc, getDoc, getDocs, query, writeBatch, deleteDoc, orderBy, limit, serverTimestamp, increment, runTransaction, where } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, isSupported, logEvent, setUserId, setUserProperties, setAnalyticsCollectionEnabled } from 'firebase/analytics';

const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const AllActivityPage = React.lazy(() => import('./pages/AllActivityPage').then(module => ({ default: module.AllActivityPage })));
const AddLeadModal = React.lazy(() => import('./components/modals/AddLeadModal'));
const LeadsTable = React.lazy(() => import('./components/leads/LeadsTable'));
const GuestDrawer = React.lazy(() => import('./components/guests/GuestDrawer'));
const OutreachStudio = React.lazy(() => import('./components/outreach/OutreachStudio'));
const TemplateHub = React.lazy(() => import('./components/templates/TemplateHub'));
const ProfileSettingsPage = React.lazy(() => import('./pages/ProfileSettingsPage'));
const OnboardingModal = React.lazy(() => import('./components/settings/OnboardingModal'));
const NavItem = React.lazy(() => import('./components/ui/NavItem'));
const EpisodesHubPage = React.lazy(() => import('./pages/EpisodesHubPage'));
const GuestDiscovery = React.lazy(() => import('./components/guest-discovery/GuestDiscovery'));
const AuthModal = React.lazy(() => import('./components/auth/AuthModal'));
const EpisodePage = React.lazy(() => import('./pages/EpisodePage'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const LandingPageMobile = React.lazy(() => import('./pages/LandingPageMobile'));
const WaitingListPage = React.lazy(() => import('./pages/WaitingListPage'));
const AuthGate = React.lazy(() => import('./components/auth/AuthGate'));
const GuestPortalCompact = React.lazy(() => import('./pages/GuestPortal'));
const TokenHelper = React.lazy(() => import('./pages/TokenHelper'));



// AI Service Imports

// Configure PDF.js worker

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};


// --- App ID for Firestore path ---
const appId = process.env.REACT_APP_FIREBASE_PROJECT_ID

// --- Initial Data (imported) ---



// --- Main Application Components ---
// --- REUSABLE COMPONENTS ---





// Helper hook for mobile detection
// Helper hook for mobile detection (imported)


// Helper component for Episode Route to handle ID lookup
// Defined outside App to prevent re-mounting on re-renders
// EpisodeRoute (imported)

export default function App() {
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const safeNavigate = (to, options) => startTransition(() => navigate(to, options));
  const location = useLocation();
  // Helper to determine active view from path
  const getActiveView = (pathname) => {
    if (pathname === '/') return 'Landing';
    if (pathname.startsWith('/dashboard')) return 'Dashboard';
    if (pathname.startsWith('/guests')) return 'Guests';
    if (pathname.startsWith('/discovery')) return 'GuestDiscovery';
    if (pathname.startsWith('/templates')) return 'Templates';
    if (pathname.startsWith('/outreach')) return 'OutreachStudio';
    if (pathname.startsWith('/settings')) return 'Settings';
    if (pathname.startsWith('/activity')) return 'AllActivity';
    if (pathname.startsWith('/distribute') || pathname.startsWith('/episodes')) return 'Episodes';
    return 'Dashboard';
  };
  const activeView = getActiveView(location.pathname);
  // Determine if we are in the "Studio" (Episode Page) to hide standard dashboard layout
  const isStudioRoute = !!location.pathname.match(/^\/episodes\/[^/]+$/);

  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [functions, setFunctions] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [outreachLead, setOutreachLead] = useState(null);
  const [discoveryLead, setDiscoveryLead] = useState(null); // New state for discovery flow
  const [discoveryMaterials, setDiscoveryMaterials] = useState(null); // New state for discovery materials
  const [bulkOutreachLeads, setBulkOutreachLeads] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isAddLeadModalOpen, setAddLeadModalOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); // 'signup' or 'login'

  // Notification system state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const isMobile = useIsMobile();

  // Real-time conversion funnel data
  // Real-time conversion funnel data
  const [funnelStats, setFunnelStats] = useState({
    totalLeads: 0,
    statusCounts: {
      'New': 0,
      'Contacted': 0,
      'Responded': 0,
      'Scheduled': 0,
      'Declined': 0,
      'Identified': 0
    }
  });

  // Calculate conversion rates from stats
  const conversionData = [
    { stage: 'New', value: funnelStats.statusCounts['new'] || 0, conversionRate: funnelStats.totalLeads > 0 ? Math.round(((funnelStats.statusCounts['new'] || 0) / funnelStats.totalLeads) * 100) : 0 },
    { stage: 'Contacted', value: funnelStats.statusCounts['contacted'] || 0, conversionRate: funnelStats.totalLeads > 0 ? Math.round(((funnelStats.statusCounts['contacted'] || 0) / funnelStats.totalLeads) * 100) : 0 },
    { stage: 'Responded', value: funnelStats.statusCounts['responded'] || 0, conversionRate: funnelStats.totalLeads > 0 ? Math.round(((funnelStats.statusCounts['responded'] || 0) / funnelStats.totalLeads) * 100) : 0 },
    { stage: 'Scheduled', value: funnelStats.statusCounts['scheduled'] || 0, conversionRate: funnelStats.totalLeads > 0 ? Math.round(((funnelStats.statusCounts['scheduled'] || 0) / funnelStats.totalLeads) * 100) : 0 },
    { stage: 'Declined', value: funnelStats.statusCounts['declined'] || 0, conversionRate: funnelStats.totalLeads > 0 ? Math.round(((funnelStats.statusCounts['declined'] || 0) / funnelStats.totalLeads) * 100) : 0 }
  ];

  // Scheduled Leads State (Separate from Recents)
  const [scheduledLeadsRaw, setScheduledLeadsRaw] = useState([]);

  // Notification system effect - using real activity data
  useEffect(() => {
    if (!db || !user) return;

    // Listen to real user activity for notifications
    const activityQuery = query(
      collection(db, 'tenants', user.uid, 'system', 'activity', 'items'),
      orderBy('createdAt', 'desc'),
      limit(20) // Get last 20 activities
    );

    const unsubActivity = onSnapshot(activityQuery,
      (snapshot) => {
        const activities = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Load read status from localStorage
        let readIds = [];
        try {
          const saved = localStorage.getItem(`notifications_read_${user?.uid}`);
          if (saved) {
            readIds = JSON.parse(saved);
          }
        } catch (error) {
        }

        // Convert activities to notifications
        const notificationsFromActivity = activities.map(activity => {
          const timestamp = activity.createdAt?.toDate?.() || new Date();

          // Map activity types to notification data
          switch (activity.type) {
            case 'email_sent':
              return {
                id: activity.id,
                type: 'email_sent',
                title: 'Email Sent',
                message: `Email sent to ${activity.message || 'lead'}`,
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_lead',
                leadId: activity.leadId
              };

            case 'email_bulk_sent':
              return {
                id: activity.id,
                type: 'bulk_email',
                title: 'Bulk Emails Sent',
                message: `Bulk outreach sent to multiple leads`,
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_leads'
              };

            case 'meeting_scheduled':
              return {
                id: activity.id,
                type: 'meeting_scheduled',
                title: 'Meeting Scheduled',
                message: `Interview confirmed: ${activity.message || 'New guest'}`,
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_episode',
                leadId: activity.leadId
              };

            case 'lead_created':
              return {
                id: activity.id,
                type: 'lead_created',
                title: 'New Lead Added',
                message: activity.message || 'New prospect added to your pipeline',
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_lead',
                leadId: activity.leadId
              };

            case 'reply_positive':
              return {
                id: activity.id,
                type: 'email_response',
                title: 'Positive Response',
                message: `Great news! ${activity.message || 'Lead responded positively'}`,
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_lead',
                leadId: activity.leadId
              };

            case 'reply_neutral':
              return {
                id: activity.id,
                type: 'email_response',
                title: 'Email Response',
                message: activity.message || 'Lead responded to your outreach',
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_lead',
                leadId: activity.leadId
              };

            case 'reply_negative':
              return {
                id: activity.id,
                type: 'email_response',
                title: 'Response Received',
                message: activity.message || 'Lead responded to your outreach',
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_lead',
                leadId: activity.leadId
              };

            case 'guest_portal':
              const getGuestPortalIcon = (subType) => {
                const iconMap = {
                  'submission': <User size={16} className="text-indigo-600" />,
                  'message': <MessageSquare size={16} className="text-blue-600" />,
                  'feedback': <Star size={16} className="text-yellow-600" />,
                  'scheduling': <Calendar size={16} className="text-purple-600" />,
                  'file_upload': <Paperclip size={16} className="text-green-600" />,
                  'file_delete': <FileX size={16} className="text-red-600" />
                };
                return iconMap[subType] || <Bell size={16} className="text-gray-600" />;
              };

              const formatSubType = (subType) => {
                const typeMap = {
                  'submission': 'Submission',
                  'message': 'Message',
                  'feedback': 'Feedback',
                  'scheduling': 'Scheduling',
                  'file_upload': 'File_upload',
                  'file_delete': 'File_delete'
                };
                return typeMap[subType] || subType?.charAt(0).toUpperCase() + subType?.slice(1) || 'Activity';
              };

              return {
                id: activity.id,
                type: 'guest_portal',
                subType: activity.subType,
                title: `Guest Portal - ${formatSubType(activity.subType)}`,
                message: typeof activity.message === 'string'
                  ? activity.message
                  : typeof activity.message === 'object' && activity.message
                    ? 'Guest portal activity (data error)'
                    : 'Guest portal activity',
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_episode',
                episodeId: activity.episodeId,
                icon: getGuestPortalIcon(activity.subType),
                isGuestPortal: true
              };

            default:
              return {
                id: activity.id,
                type: 'activity',
                title: 'Activity Update',
                message: activity.message || 'New activity in your pipeline',
                timestamp,
                read: readIds.includes(activity.id),
                action: 'view_leads'
              };
          }
        });

        setNotifications(notificationsFromActivity);
      }, (error) => {
      });

    return () => unsubActivity();
  }, [db, user]);

  useEffect(() => {
    try {
      // Initialize Firebase
      const app = initializeApp(firebaseConfig);
      const authInstance = getAuth(app);
      const dbInstance = getFirestore(app);
      const functionsInstance = getFunctions(app, 'us-central1');

      // Debug: Log functions instance

      // Initialize Analytics (guard for environments/browsers that don't support it)
      if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
        isSupported().then((supported) => {
          if (supported) {
            const analytics = getAnalytics(app);
            // Ensure collection enabled (useful in dev)
            setAnalyticsCollectionEnabled(analytics, true);
            // Initial page_view
            logEvent(analytics, 'page_view', {
              page_location: window.location.href,
              page_path: window.location.pathname,
              debug_mode: true
            });
            // Emit a debug init event to surface in DebugView immediately
            logEvent(analytics, 'debug_init', { debug_mode: true });
          }
        });
      }

      setAuth(authInstance);
      setDb(dbInstance);
      setFunctions(functionsInstance);

      // Set up auth state listener
      const unsubscribe = onAuthStateChanged(authInstance, (user) => {
        setUser(user);
        setIsAuthReady(true);
        // Tie analytics to user when available
        if (user && typeof window !== 'undefined' && firebaseConfig.measurementId) {
          isSupported().then((supported) => {
            if (!supported) return;
            const analytics = getAnalytics();
            setUserId(analytics, user.uid);
            setUserProperties(analytics, { email: user.email || undefined });
          });
        }
      });

      // Cleanup subscription on unmount
      return () => unsubscribe();

    } catch (error) {
    }
  }, []); // Empty dependency array ensures this runs only once

  // Auto-redirect authenticated users to dashboard
  useEffect(() => {
    if (user && isAuthReady && activeView === 'Landing') {
      const params = new URLSearchParams(window.location.search);
      // Check for OAuth callback params
      if (params.get('success') === 'calcom_connected' || params.get('error')) {
        safeNavigate('/settings');
      } else {
        if (location.pathname === '/' || location.pathname === '/landing') {
          safeNavigate('/dashboard');
        }
      }
    }
    // Log page view on route/view change
    if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
      isSupported().then((supported) => {
        if (!supported) return;
        const analytics = getAnalytics();
        logEvent(analytics, 'page_view', {
          page_location: window.location.href,
          page_path: '/' + String(activeView || 'Dashboard').toLowerCase(),
          debug_mode: true
        });
      });
    }
  }, [user, isAuthReady, activeView]);

  const handleOnboardingComplete = async (formData) => {
    if (!db || !user) return;

    try {
      // 1. Create user settings
      const settingsRef = doc(db, `tenants/${user.uid}/settings`, 'app');
      const newSettings = {
        ...formData,
        hasCompletedOnboarding: true,
        // Ensure all fields from the new settings page are initialized
        schedulingLink: formData.schedulingLink || ''
      };
      await setDoc(settingsRef, newSettings);

      // 2. Initialize analytics collections with placeholder documents
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      // Initialize monthly analytics
      const monthlyAnalyticsRef = doc(db, `tenants/${user.uid}/analytics/monthly/items`, monthKey);
      await setDoc(monthlyAnalyticsRef, {
        month: monthKey,
        totalLeads: 0,
        totalOutreach: 0,
        totalReplies: 0,
        totalScheduled: 0,
        conversionRate: 0,
        responseRate: 0,
        timestamp: now.toISOString(),
        createdAt: now.toISOString()
      });


      setSettings(newSettings);
      setShowOnboarding(false);
    } catch (error) {
    }
  };

  useEffect(() => {
    if (!isAuthReady || !db || !user) return;

    // --- Settings listener ---
    const settingsRef = doc(db, `tenants/${user.uid}/settings`, 'app');
    const unsubSettings = onSnapshot(settingsRef, (doc) => {
      if (doc.exists() && doc.data().hasCompletedOnboarding) {
        setSettings(doc.data());
        setShowOnboarding(false);
      } else {
        setSettings({});
        setShowOnboarding(true); // Explicitly set to true
      }
    }, (error) => {
      setShowOnboarding(true); // Show onboarding on error to be safe
    });




    // --- Seed Data Logic (only runs if user document is missing) ---
    const seedData = async () => {
      const userDocRef = doc(db, `tenants/${user.uid}`);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        const batch = writeBatch(db);

        const leadsCollectionRef = collection(db, `tenants/${user.uid}/contacts`);
        INITIAL_LEADS.forEach(lead => {
          const newLeadRef = doc(leadsCollectionRef);
          // Add required timestamp fields for queries
          batch.set(newLeadRef, {
            ...lead,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        });

        const templatesCollectionRef = collection(db, `tenants/${user.uid}/content/templates/items`);
        INITIAL_TEMPLATES.forEach(template => {
          const newTemplateRef = doc(templatesCollectionRef);
          batch.set(newTemplateRef, {
            ...template,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        });

        batch.set(userDocRef, { initialized: true });
        await batch.commit();
      }
    };
    seedData();

    // --- Data Listeners (Optimized) ---
    // 1. Funnel Stats Listener (Single Document Read)
    const statsRef = doc(db, `tenants/${user.uid}/analytics/funnel`);
    const unsubStats = onSnapshot(statsRef, async (statsDoc) => {
      if (statsDoc.exists()) {
        setFunnelStats(statsDoc.data());
      } else {
        // Self-Healing: Calculate and create stats if missing
        const allLeadsSnap = await getDocs(collection(db, `tenants/${user.uid}/contacts`));
        // Use lowercase status values (standardized schema)
        const counts = { 'new': 0, 'contacted': 0, 'responded': 0, 'scheduled': 0, 'declined': 0, 'identified': 0 };
        allLeadsSnap.forEach(d => {
          const s = d.data().status || 'new';
          counts[s] = (counts[s] || 0) + 1;
        });
        const initialStats = {
          totalLeads: allLeadsSnap.size,
          statusCounts: counts,
          lastUpdated: serverTimestamp()
        };
        await setDoc(statsRef, initialStats);
        // State will update on next snapshot emission
      }
    });

    // 2. Scheduled Leads (for Routing/Hub) - Global but generally smaller subset
    // We listen to leads with status in active production cycle to power the Episode Hub.
    // Limit to 100 to prevent unbounded reads - pagination should be used for larger datasets
    const scheduledQuery = query(
      collection(db, `tenants/${user.uid}/contacts`),
      where('status', 'in', ['scheduled', 'prepped', 'recorded', 'published', 'post-production']),
      limit(100)
    );
    const unsubScheduled = onSnapshot(scheduledQuery, (snap) => {
      const data = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setScheduledLeadsRaw(data);
    });

    // 3. Recent Leads for Dashboard
    const recentLeadsQuery = query(collection(db, `tenants/${user.uid}/contacts`), orderBy('createdAt', 'desc'), limit(10));
    const unsubRecents = onSnapshot(recentLeadsQuery, (snap) => {
      const recentData = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setLeads(recentData); // This populates 'leads' with ONLY recent ones.
    });

    const templatesQuery = query(
      collection(db, `tenants/${user.uid}/content/templates/items`),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );

    const unsubTemplates = onSnapshot(templatesQuery, (querySnapshot) => {
      const templatesData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, id: doc.id }; // Ensure Firestore ID overwrites any 'id' in data
      });
      setTemplates(templatesData);
    }, (error) => {
      console.error('Templates query error:', error);
    });

    return () => {
      unsubSettings();
      unsubStats();
      unsubScheduled();
      unsubRecents();
      unsubTemplates();
    };
  }, [isAuthReady, db, user]);



  const handleAddProspects = async (prospects) => {
    if (!db || !user) return;
    try {
      const batch = writeBatch(db);
      const leadsCollectionPath = `tenants/${user.uid}/contacts`;

      prospects.forEach(prospect => {
        const { name, companyName, description, email, linkedin } = prospect;
        const newLeadRef = doc(collection(db, leadsCollectionPath));

        // Prepare data with standardized fields
        const leadData = prepareForStorage({
          founderName: name, // Will be split into firstName/lastName
          companyName,
          guestBio: description || '',
          companyDescription: '',
          stage: 'Not Contacted',
          lastContacted: null,
          email: email || '',
          linkedin: linkedin || ''
        });

        batch.set(newLeadRef, leadData);
      });

      await batch.commit();
      safeNavigate('/guests');
    } catch (error) {
    }
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    if (!db || !user) return;
    try {
      const leadRef = doc(db, `tenants/${user.uid}/contacts`, leadId);
      const statsRef = doc(db, `tenants/${user.uid}/analytics/funnel`);

      // Normalize status to lowercase for storage
      const normalizedStatus = getStatusForStorage(newStatus);

      await runTransaction(db, async (transaction) => {
        const leadDoc = await transaction.get(leadRef);
        if (!leadDoc.exists()) throw new Error("Lead does not exist!");

        const oldStatus = leadDoc.data().status || 'new';

        // 1. Update Lead
        transaction.update(leadRef, {
          status: normalizedStatus,
          lastContacted: new Date().toISOString().split('T')[0],
          updatedAt: new Date().toISOString()
        });

        // 2. Update Stats (Atomic)
        // If status didn't change, skip stats update
        if (oldStatus !== normalizedStatus) {
          transaction.set(statsRef, {
            statusCounts: {
              [oldStatus]: increment(-1),
              [normalizedStatus]: increment(1)
            },
            lastUpdated: serverTimestamp()
          }, { merge: true });
        }
      });
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: normalizedStatus }));
      }
      if (normalizedStatus === 'scheduled') {
        // Fetch lead to include name/company in the activity
        let leadName = '';
        let companyName = '';
        try {
          const leadSnapshot = await getDoc(doc(db, `tenants/${user.uid}/contacts`, leadId));
          if (leadSnapshot.exists()) {
            const data = leadSnapshot.data();
            leadName = getFullName(data);
            companyName = getCompanyName(data);
          }
        } catch (e) {
          // Non-blocking if fetch fails
        }
        // Log Recent Activity: meeting scheduled with context
        await addDoc(collection(db, 'tenants', user.uid, 'system', 'activity', 'items'), {
          type: 'meeting_scheduled',
          title: 'Interview scheduled',
          message: [leadName, companyName].filter(Boolean).join(' — '),
          leadId,
          createdAt: serverTimestamp(),
          actorUserId: user.uid
        });
        safeNavigate('/episodes');
      }
    } catch (error) {
    }
  };

  const handleUpdateLeadNotes = async (leadId, newNotes, fitScore, fitRationale) => {
    if (!db || !user) return;
    try {
      const leadRef = doc(db, `tenants/${user.uid}/contacts`, leadId);
      const dataToUpdate = { notes: newNotes }; // Keep for backward compatibility
      if (fitScore) dataToUpdate.fitScore = fitScore;
      if (fitRationale) dataToUpdate.fitRationale = fitRationale;
      await updateDoc(leadRef, dataToUpdate);

      // SYNC TO EPISODE
      try {
        const episodeRef = doc(db, `tenants/${user.uid}/content/episodes/items`, leadId);
        await updateDoc(episodeRef, dataToUpdate);
      } catch (syncError) {
      }
    } catch (error) {
    }
  };

  const handleUpdateLeadBio = async (leadId, guestBio, companyDescription) => {
    if (!db || !user) return;
    try {
      const leadRef = doc(db, `tenants/${user.uid}/contacts`, leadId);
      const dataToUpdate = {};
      if (guestBio !== undefined) {
        dataToUpdate.guestBio = guestBio; // Legacy field
        dataToUpdate.bio = guestBio; // New field
      }
      if (companyDescription !== undefined) {
        dataToUpdate.companyDescription = companyDescription; // Same in both schemas
      }
      await updateDoc(leadRef, dataToUpdate);

      // SYNC TO EPISODE
      try {
        const episodeRef = doc(db, `tenants/${user.uid}/content/episodes/items`, leadId);
        await updateDoc(episodeRef, dataToUpdate);
      } catch (syncError) {
      }
    } catch (error) {
    }
  };

  const handlePrepareOutreach = (lead) => {
    setOutreachLead(lead);
    safeNavigate('/outreach');
    setSelectedLead(null); // Close modal
  };

  const handlePrepareBulkOutreach = (leadIds) => {
    const selected = leads.filter(lead => leadIds.includes(lead.id));
    setBulkOutreachLeads(selected);
    safeNavigate('/outreach');
  };

  const handleNavigateToOutreach = (guest, materials) => {
    // Fast-track: Open the Dossier instead of switching views
    setSelectedLead(guest);
    setDiscoveryMaterials(materials);
  };

  const handleAddLead = async (newLead) => {
    if (!db || !user) {
      throw new Error('Database or user not available');
    }

    try {
      const leadsCollectionRef = collection(db, `tenants/${user.uid}/contacts`);
      // Remove 'id' if present so it doesn't conflict with Firestore ID or cause duplication issues
      const { id, ...leadData } = newLead;

      const ref = await addDoc(leadsCollectionRef, {
        ...leadData,
        status: 'new', // Use 'new' for newly discovered guests
        createdAt: serverTimestamp(), // Use createdAt for consistent sorting
        updatedAt: serverTimestamp(),
      });

      // Update Funnel Stats
      const statsRef = doc(db, `tenants/${user.uid}/analytics/funnel`);
      await setDoc(statsRef, {
        totalLeads: increment(1),
        statusCounts: {
          'new': increment(1) // Use lowercase to match schema
        },
        lastUpdated: serverTimestamp()
      }, { merge: true });

      // Log Recent Activity: lead created
      const guestName = getFullName(newLead) || 'Potential guest';
      const companyName = getCompanyName(newLead) || 'Independent creator';
      await addDoc(collection(db, 'tenants', user.uid, 'system', 'activity', 'items'), {
        type: 'lead_created',
        title: 'New guest added',
        message: [guestName, companyName].filter(name => name && name !== 'Potential guest' && name !== 'Independent creator').join(' — '),
        leadId: ref.id,
        createdAt: serverTimestamp(),
        actorUserId: user.uid
      });

      // Return success - modal will handle closing
      return { success: true, leadId: ref.id };
    } catch (error) {
      throw error; // Let modal handle the error
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!db || !user) return;
    try {
      const leadRef = doc(db, `tenants/${user.uid}/contacts`, leadId);
      const statsRef = doc(db, `tenants/${user.uid}/analytics/funnel`);

      await runTransaction(db, async (transaction) => {
        const leadDoc = await transaction.get(leadRef);
        if (!leadDoc.exists()) return; // Already deleted

        const status = leadDoc.data().status || 'new';

        transaction.delete(leadRef);

        transaction.set(statsRef, {
          totalLeads: increment(-1),
          statusCounts: {
            [status]: increment(-1)
          },
          lastUpdated: serverTimestamp()
        }, { merge: true });
      });
    } catch (error) {
      throw error;
    }
  };

  // Delete episode from both contacts and episodes collections
  const handleDeleteEpisode = async (episodeId) => {
    if (!db || !user) return;
    try {
      // Delete from contacts collection
      const contactRef = doc(db, `tenants/${user.uid}/contacts`, episodeId);
      const statsRef = doc(db, `tenants/${user.uid}/analytics/funnel`);

      // Delete from episodes collection
      const episodeRef = doc(db, `tenants/${user.uid}/content/episodes/items`, episodeId);

      await runTransaction(db, async (transaction) => {
        const contactDoc = await transaction.get(contactRef);
        const episodeDoc = await transaction.get(episodeRef);

        // Delete from both collections if they exist
        if (contactDoc.exists()) {
          const status = contactDoc.data().status || 'new';
          transaction.delete(contactRef);
          transaction.set(statsRef, {
            totalLeads: increment(-1),
            statusCounts: {
              [status]: increment(-1)
            },
            lastUpdated: serverTimestamp()
          }, { merge: true });
        }

        if (episodeDoc.exists()) {
          transaction.delete(episodeRef);
        }
      });
    } catch (error) {
      console.error('Error deleting episode:', error);
      throw error;
    }
  };

  const handleBulkDeleteLeads = async (leadIds) => {
    if (!db || !user) return;
    try {
      // To correctly update stats, we need to know the status of each deleted lead.
      // We'll read them in parallel chunks to minimize time, though it costs reads.
      // Optimization: Fetch only 'status' field.
      // But for bulk delete of large numbers, we might just want to 'recalculate' stats later?
      // No, let's try to be accurate. 
      await runTransaction(db, async (transaction) => {
        const statsRef = doc(db, `tenants/${user.uid}/analytics/funnel`);
        const statusDecrements = {};
        let totalDeleted = 0;

        // Note: Transaction has limit on reads/writes. If leadIds is huge (>500), this will fail.
        // Assuming leadIds is reasonable (<50).
        for (const leadId of leadIds) {
          const leadRef = doc(db, `tenants/${user.uid}/contacts`, leadId);
          const leadDoc = await transaction.get(leadRef);
          if (leadDoc.exists()) {
            const s = leadDoc.data().status || 'New';
            statusDecrements[s] = (statusDecrements[s] || 0) - 1;
            totalDeleted++;
            transaction.delete(leadRef);
          }
        }

        const statsUpdate = {
          totalLeads: increment(-totalDeleted),
          statusCounts: {},
          lastUpdated: serverTimestamp()
        };

        Object.keys(statusDecrements).forEach(s => {
          statsUpdate.statusCounts[s] = increment(statusDecrements[s]);
        });

        transaction.set(statsRef, statsUpdate, { merge: true });
      });

    } catch (error) {
      throw error;
    }
  };

  // Handle CSV Import with Optimization Service
  const handleCSVImport = async (importedLeads) => {
    if (!db || !user) return;

    try {

      // Dynamic import of the service
      const { processImport } = await import('./services/importService');

      const result = await processImport(
        importedLeads,
        leads, // Pass existing leads for duplicate checking
        db,
        user,
        (percentage, message) => {
          // Optional: Add a toast or notification here for real-time feedback
        }
      );

      const { imported, duplicates, skipped } = result;

      // Show success message with detailed stats
      if (duplicates > 0) {
        alert(`Import completed! ${imported} new leads imported. ${duplicates} duplicates skipped${skipped > 0 ? `, ${skipped} invalid/skipped` : ''}.`);
      } else {
        alert(`Import completed! ${imported} leads imported successfully.`);
      }

    } catch (error) {
      alert(`Error importing leads: ${error.message}`);
    }
  };

  const handleSaveSettings = async (newSettings) => {
    if (!db || !user) return;
    try {
      const settingsRef = doc(db, `tenants/${user.uid}/settings`, 'app');
      // Make sure to keep the onboarding flag
      const dataToSave = { ...settings, ...newSettings };
      await setDoc(settingsRef, dataToSave, { merge: true });
      setSettings(dataToSave);
      alert("Settings saved!");
    } catch (error) {
    }
  };

  // Simple notification functions
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const markNotificationAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );

    // Save read status to localStorage
    try {
      const readIds = notifications
        .filter(n => n.read || n.id === notificationId)
        .map(n => n.id);
      localStorage.setItem(`notifications_read_${user?.uid}`, JSON.stringify(readIds));
    } catch (error) {
    }
  };

  const markAllNotificationsAsRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );

    // Save all as read to localStorage
    try {
      const allIds = notifications.map(n => n.id);
      localStorage.setItem(`notifications_read_${user?.uid}`, JSON.stringify(allIds));
    } catch (error) {
    }
  };

  const handleNotificationAction = (notification) => {
    markNotificationAsRead(notification.id);
    setShowNotifications(false);

    // Handle different notification actions
    switch (notification.action) {
      case 'view_lead':
        safeNavigate('/guests');
        if (notification.leadId) {
          const lead = leads.find(l => l.id === notification.leadId);
          if (lead) {
            setSelectedLead(lead);
          }
        }
        break;
      case 'view_episode':
        safeNavigate('/episodes');
        // If it's a guest portal notification with an episodeId, navigate to that episode
        if (notification.episodeId && notification.isGuestPortal) {
          const episodes = leads.filter(lead => lead.status === 'scheduled');
          const episode = episodes.find(ep => ep.id === notification.episodeId);
          if (episode) {
            setSelectedEpisode(episode);
          }
        }
        break;
      case 'view_leads':
        safeNavigate('/guests');
        break;
      default:
        break;
    }
  };

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  // Computed unread count for cleaner code
  const unreadCount = notifications.filter(n => !n.read).length;

  const handleSendEmail = async (emailData) => {
    try {
      // Add user's email settings to the request
      const emailDataWithSettings = {
        ...emailData,
        to: emailData.recipientEmail, // Map recipientEmail to 'to' for backend
        senderEmail: settings?.podcastEmail || user?.email,
        senderName: `${settings?.firstName || 'Host'} ${settings?.lastName || 'Name'}`,
        podcastName: settings?.podcastName || 'My Podcast'
      };

      const emailService = await import('./services/emailService');
      const response = await emailService.default.sendEmail(emailDataWithSettings);

      if (response.success) {
        // Log Recent Activity: single email sent
        await addDoc(collection(db, 'tenants', user.uid, 'system', 'activity', 'items'), {
          type: 'email_sent',
          title: 'Email sent',
          message: `To ${emailDataWithSettings.founderName || emailDataWithSettings.companyName || emailDataWithSettings.recipientEmail}`,
          leadId: emailDataWithSettings.leadId,
          createdAt: serverTimestamp(),
          actorUserId: user.uid
        });
        return response;
      } else {
        throw new Error(response.error || 'Failed to send email');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleSendBulkEmails = async (bulkData) => {
    try {
      // Add user's email settings to the request
      const bulkDataWithSettings = {
        ...bulkData,
        senderEmail: settings?.podcastEmail || user?.email,
        senderName: `${settings?.firstName || 'Host'} ${settings?.lastName || 'Name'}`,
        podcastName: settings?.podcastName || 'My Podcast'
      };

      const emailService = await import('./services/emailService');
      const response = await emailService.default.sendBulkEmails(bulkDataWithSettings);

      if (response.success) {
        // Log Recent Activity: bulk outreach
        await addDoc(collection(db, 'tenants', user.uid, 'system', 'activity', 'items'), {
          type: 'email_bulk_sent',
          title: 'Bulk outreach sent',
          message: `Sent to ${bulkDataWithSettings.emails?.length || 0} leads`,
          count: bulkDataWithSettings.emails?.length || 0,
          createdAt: serverTimestamp(),
          actorUserId: user.uid
        });
        return response;
      } else {
        throw new Error(response.error || 'Failed to send bulk emails');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleSignOut = async () => {
    if (auth) {
      await signOut(auth);
      setSettings(null); // Clear settings on sign out
      safeNavigate('/'); // Redirect to landing
    }
  }
  // Authentication handlers for landing page
  const handleSignUp = () => {
    if (!auth) {
      return;
    }
    startTransition(() => {
      setAuthMode('signup');
      setShowAuthModal(true);
    });
  };

  const handleLogin = () => {
    if (!auth) {
      return;
    }
    startTransition(() => {
      setAuthMode('login');
      setShowAuthModal(true);
    });
  };

  const handleAuthSuccess = () => {
    startTransition(() => {
      setShowAuthModal(false);
      safeNavigate('/dashboard'); // Redirect to dashboard after successful auth
    });
  };



  if (!isAuthReady || (user && settings === null)) {
    return <div className="flex h-screen items-center justify-center"><Loader className="animate-spin" /></div>
  }

  // Show auth modal if needed (check this FIRST)
  if (showAuthModal) {
    return (
      <AuthModal
        mode={authMode}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        auth={auth}
      />
    );
  }



  // Handle authentication flow
  // Bypass auth for Guest Portal
  if (location.pathname.startsWith('/guest/') || location.pathname.startsWith('/portal/')) {
    return (
      <React.Suspense fallback={<div className="h-screen w-full flex items-center justify-center"><Loader className="animate-spin" /></div>}>
        <GuestPortalCompact />
      </React.Suspense>
    );
  }

  // Bypass auth for Waiting List page
  if (location.pathname === '/waitlist') {
    return (
      <React.Suspense fallback={<div className="h-screen w-full flex items-center justify-center"><Loader className="animate-spin" /></div>}>
        <WaitingListPage />
      </React.Suspense>
    );
  }



  if (!user) {
    // If no user, show landing page (Mobile or Desktop)
    if (isMobile) {
      return (
        <LandingPageMobile
          onSignUp={handleSignUp}
          onLogin={handleLogin}
        />
      );
    }
    return (
      <LandingPage
        onSignUp={handleSignUp}
        onLogin={handleLogin}
      />
    );
  }

  // Show onboarding modal if required
  if (showOnboarding) {
    return <OnboardingModal user={user} onComplete={handleOnboardingComplete} />
  }

  // Pre-calculate scheduled leads for use in routes
  const scheduledLeads = scheduledLeadsRaw;
  const activeLeads = []; // Deprecated: LeadsTable must fetch its own data now. Passing empty to force refactor if missed.





  // Main routing logic
  if (activeView === 'Landing') {
    if (isMobile) {
      return (
        <LandingPageMobile
          onSignUp={handleSignUp}
          onLogin={handleLogin}
        />
      );
    }
    return (
      <LandingPage
        onSignUp={handleSignUp}
        onLogin={handleLogin}
      />
    );
  }



  // Main app layout for authenticated users
  return (
    <ThemeProvider settings={settings}>
      <Suspense fallback={<div className="flex h-screen w-full items-center justify-center bg-[#FDFCFB]"><Loader className="animate-spin text-blue-600" size={32} /></div>}>
        <div className="flex h-screen bg-[#FDFCFB] font-sans overflow-hidden p-4 gap-4">
          {/* Floating Dock Navigation - Hide in Studio Mode */}
          {!isStudioRoute && (
            <div className={`${sidebarCollapsed ? 'w-24' : 'w-72'} dock-container flex flex-col relative transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]`}>

              {/* Logo Section */}
              <div className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4 cursor-pointer group" onClick={() => safeNavigate('/dashboard')}>
                  <div className="flex-shrink-0 relative">
                    <BrandLogo settings={settings} size="w-14 h-14" className="rounded-2xl shadow-lg shadow-blue-500/30 transform group-hover:scale-110 transition-all duration-300" />
                  </div>
                  {!sidebarCollapsed && (
                    <div className="flex-1 min-w-0">
                      <h1 className="text-md font-bold text-gray-800 uppercase tracking-wide">
                        {settings?.podcastName || 'PodGuestly'}
                      </h1>
                      <p className="text-[10px] text-gray-500 font-bold bg-blue-100 text-blue-600 inline-block px-2 py-0.5 rounded-full mt-1">STUDIO</p>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                >
                  <ChevronLeft className={`w-4 h-4 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* User Profile Section */}
              <div className="mx-4 mb-2 p-3 rounded-2xl bg-white/50 border border-white/60 shadow-sm backdrop-blur-sm">
                <div className="flex items-center gap-3 group cursor-pointer">
                  <div className="w-8 h-8 rounded bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white shadow-lg shadow-indigo-500/20">
                    {(settings?.firstName?.[0] || user.displayName?.[0] || user.email?.[0] || 'U').toUpperCase()}
                  </div>
                  {!sidebarCollapsed && (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800 truncate">{settings?.firstName || 'Host'}</p>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)] animate-pulse"></div>
                        <p className="text-[10px] text-gray-500 font-medium">Online</p>
                      </div>
                    </div>
                  )}
                  {!sidebarCollapsed && (
                    <div
                      onClick={() => safeNavigate('/settings')}
                      className="cursor-pointer group flex items-center gap-2"
                      title="Settings"
                    >
                      <Settings size={16} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Menu */}
              <nav className="flex-1 overflow-y-auto py-6 space-y-8 no-scrollbar">
                <div>
                  {!sidebarCollapsed && (
                    <p className="px-6 mb-3 text-[10px] font-bold text-gray-600 uppercase tracking-widest font-mono">Main Console</p>
                  )}
                  <div className="space-y-0.5">
                    <NavItem icon={<LayoutDashboard size={18} />} text="Dashboard" active={activeView === 'Dashboard'} onClick={() => safeNavigate('/dashboard')} collapsed={sidebarCollapsed} />
                    <NavItem icon={<Users size={18} />} text="Guest Grid" active={activeView === 'Guests'} onClick={() => safeNavigate('/guests')} badge={leads.filter(l => l.status === 'New').length > 0 ? leads.filter(l => l.status === 'New').length : null} collapsed={sidebarCollapsed} />
                    <NavItem icon={<Mic size={18} />} text="Episode Studio" active={activeView === 'Episodes'} onClick={() => safeNavigate('/episodes')} collapsed={sidebarCollapsed} />
                  </div>
                </div>

                <div>
                  {!sidebarCollapsed && (
                    <p className="px-6 mb-3 text-[10px] font-bold text-gray-600 uppercase tracking-widest font-mono">Tools</p>
                  )}
                  <div className="space-y-0.5">
                    <NavItem icon={<Search size={18} />} text="Discovery" active={activeView === 'GuestDiscovery'} onClick={() => safeNavigate('/discovery')} collapsed={sidebarCollapsed} />
                    <NavItem icon={<MessageSquare size={18} />} text="Templates" active={activeView === 'Templates'} onClick={() => safeNavigate('/templates')} collapsed={sidebarCollapsed} />
                    <NavItem icon={<Send size={18} />} text="Outreach" active={activeView === 'OutreachStudio'} onClick={() => safeNavigate('/outreach')} collapsed={sidebarCollapsed} />
                  </div>
                </div>
              </nav>

              <div className="p-4 mt-auto">
                <button onClick={handleSignOut} className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} w-full p-3 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all font-medium`}>
                  <LogOut size={16} />
                  {!sidebarCollapsed && <span className="text-xs font-medium">Log Out</span>}
                </button>

                {/* Cmd+K Hint */}
                {!sidebarCollapsed && (
                  <div className="mt-4 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-between group cursor-pointer hover:bg-white hover:shadow-sm transition-all">
                    <div className="flex items-center gap-2 text-gray-500 group-hover:text-blue-600">
                      <Search size={14} />
                      <span className="text-xs">Search</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-bold text-gray-400 bg-white px-1.5 py-0.5 rounded border border-gray-200">⌘K</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Content Area - Rounded and Spaced for Dashboard, Full for Studio */}
          <div className={isStudioRoute ? "flex-1 flex flex-col overflow-hidden bg-white relative" : "flex-1 flex flex-col overflow-hidden bg-white/50 backdrop-blur-sm rounded-[32px] border border-white/60 shadow-xl relative"}>
            {/* Top Header Bar - Hide in Studio Mode */}
            {!isStudioRoute && (
              <header className="bg-transparent px-8 py-6 sticky top-0 z-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-800 capitalize tracking-tight">
                      {activeView === 'Dashboard' ? 'Dashboard' :
                        activeView === 'Guests' ? 'Guest Management' :
                          activeView === 'Episodes' ? 'Episode Studio' :
                            activeView === 'GuestDiscovery' ? 'Discovery Studio' :

                              activeView === 'Templates' ? 'Email Templates' :
                                activeView === 'Settings' ? 'Settings' :
                                  activeView}
                    </h1>
                    <p className="text-base text-gray-500 mt-1 font-medium">
                      {activeView === 'Dashboard' ? 'Overview of your podcast booking performance' :
                        activeView === 'Guests' ? 'Manage and track your podcast opportunities' :
                          activeView === 'Episodes' ? 'Production pipeline and episode management' :
                            activeView === 'GuestDiscovery' ? 'Deep-scan search for podcast-ready thought leaders' :

                              activeView === 'Templates' ? 'Create and manage your outreach templates' :
                                activeView === 'Settings' ? 'Configure your account and preferences' :
                                  'Manage your account'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative notifications-container">
                      <button
                        onClick={() => setShowNotifications(!showNotifications)}
                        className="p-3 text-gray-400 hover:text-blue-600 rounded-full hover:bg-white hover:shadow-md transition-all relative bg-white/50 border border-white/60"
                      >
                        <Bell size={20} />
                        {unreadCount > 0 && (
                          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
                        )}
                      </button>

                      {/* Notifications Dropdown */}
                      {showNotifications && (
                        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                          <div className="p-4 border-b border-gray-100 bg-gray-50">
                            <div className="flex items-center justify-between">
                              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                              {unreadCount > 0 && (
                                <button
                                  onClick={markAllNotificationsAsRead}
                                  className="text-sm text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
                                >
                                  Mark all read
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="max-h-64 overflow-y-auto">
                            {notifications.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">
                                <Bell size={24} className="mx-auto mb-2 text-gray-300" />
                                <p>No notifications yet</p>
                              </div>
                            ) : (
                              notifications.map((notification) => (
                                <div
                                  key={notification.id}
                                  onClick={() => handleNotificationAction(notification)}
                                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${!notification.read
                                    ? notification.isGuestPortal
                                      ? 'bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-l-green-500'
                                      : 'bg-blue-50 border-l-4 border-l-blue-500'
                                    : notification.isGuestPortal
                                      ? 'hover:bg-green-25'
                                      : ''
                                    }`}
                                >
                                  <div className="flex items-start gap-3">
                                    {/* Notification type icon */}
                                    <div className="flex-shrink-0">
                                      {notification.type === 'email_sent' && (
                                        <Mail size={16} className="text-blue-500 mt-1" />
                                      )}
                                      {notification.type === 'bulk_email' && (
                                        <Send size={16} className="text-orange-500 mt-1" />
                                      )}
                                      {notification.type === 'meeting_scheduled' && (
                                        <Calendar size={16} className="text-green-500 mt-1" />
                                      )}
                                      {notification.type === 'lead_created' && (
                                        <Users size={16} className="text-blue-500 mt-1" />
                                      )}
                                      {notification.type === 'email_response' && (
                                        <MessageSquare size={16} className="text-indigo-500 mt-1" />
                                      )}
                                      {notification.type === 'activity' && (
                                        <Activity size={16} className="text-gray-500 mt-1" />
                                      )}
                                      {notification.type === 'campaign' && (
                                        <div className="mt-0.5" title={`Campaign ${notification.subType}`}>
                                          {(() => {
                                            // Campaign-specific icons based on subType
                                            const campaignIcons = {
                                              'launched': <Send size={16} className="text-green-500" />,
                                              'paused': <Clock size={16} className="text-yellow-500" />,
                                              'completed': <CheckCircle size={16} className="text-blue-500" />,
                                              'email_sent': <Send size={16} className="text-orange-500" />,
                                              'email_delivered': <CheckCircle size={16} className="text-green-500" />,
                                              'email_opened': <Mail size={16} className="text-blue-500" />,
                                              'email_clicked': <MousePointer size={16} className="text-purple-500" />,
                                              'email_replied': <MessageSquare size={16} className="text-green-500" />,
                                              'email_bounced': <AlertCircle size={16} className="text-red-500" />,
                                              'email_unsubscribed': <UserMinus size={16} className="text-red-500" />,
                                              'warming_limited': <AlertTriangle size={16} className="text-yellow-500" />,
                                              'warming_resumed': <Play size={16} className="text-green-500" />
                                            };
                                            return campaignIcons[notification.subType] || <Target size={16} className="text-indigo-500" />;
                                          })()}
                                        </div>
                                      )}
                                      {notification.type === 'guest_portal' && (
                                        <div className="mt-0.5" title={`Guest Portal ${notification.subType}`}>
                                          {notification.icon}
                                        </div>
                                      )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 leading-tight">
                                        {notification.title}
                                      </p>
                                      <p className="text-sm text-gray-600 mt-1 leading-tight">
                                        {typeof notification.message === 'string'
                                          ? notification.message
                                          : typeof notification.message === 'object' && notification.message
                                            ? 'Notification data error - please refresh'
                                            : 'No message available'
                                        }
                                      </p>
                                      <p className="text-xs text-gray-400 mt-2">
                                        {notification.timestamp.toLocaleString()}
                                      </p>
                                    </div>
                                    {!notification.read && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          markNotificationAsRead(notification.id);
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
                                      >
                                        Mark read
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          {notifications.length > 0 && (
                            <div className="p-3 border-t border-gray-100 bg-gray-50">
                              <button
                                onClick={() => setShowNotifications(false)}
                                className="w-full text-sm text-gray-600 hover:text-gray-800 font-medium cursor-pointer"
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </header>
            )}

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto">
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                <Route path="/dashboard" element={
                  <Dashboard
                    onAddLead={() => startTransition(() => setAddLeadModalOpen(true))}
                    onNavigate={(view) => {
                      // Backward compatibility for internal dashboard links
                      if (view === 'Guests') safeNavigate('/guests');
                      if (view === 'Episodes') safeNavigate('/episodes');
                      if (view === 'Settings') safeNavigate('/settings');
                      if (view === 'Templates') safeNavigate('/templates');
                      if (view === 'AllActivity') safeNavigate('/activity');
                    }}
                    leads={leads}
                    episodes={scheduledLeads}
                    onPrepareBulkOutreach={handlePrepareBulkOutreach}
                    db={db}
                    user={user}
                    conversionData={conversionData}
                    funnelStats={funnelStats}
                  />
                } />

                <Route path="/guests" element={
                  <LeadsTable
                    funnelStats={funnelStats}
                    onSelectLead={(lead) => startTransition(() => setSelectedLead(lead))}
                    onAddLead={() => startTransition(() => setAddLeadModalOpen(true))}
                    onPrepareBulkOutreach={handlePrepareBulkOutreach}
                    onDeleteLead={handleDeleteLead}
                    onBulkDeleteLeads={handleBulkDeleteLeads}
                    onCSVImport={handleCSVImport}
                    db={db}
                    user={user}
                  />
                } />

                <Route path="/discovery" element={
                  <GuestDiscovery
                    onAddLead={handleAddLead}
                    user={user}
                    onNavigateToOutreach={handleNavigateToOutreach}
                    db={db}
                  />
                } />

                <Route path="/templates" element={
                  <TemplateHub
                    templates={templates}
                    setTemplates={setTemplates}
                    settings={settings}
                    db={db}
                    user={user}
                  />
                } />

                <Route path="/outreach" element={
                  <OutreachStudio
                    leads={leads}
                    templates={templates}
                    settings={settings}
                    user={user}
                    db={db}
                    onBack={() => safeNavigate('/dashboard')}
                    lead={selectedLead || outreachLead}
                    onSendEmail={handleSendEmail}
                  />
                } />

                <Route path="/token-helper" element={
                  <TokenHelper />
                } />
                <Route path="/settings" element={
                  <div className="h-full overflow-hidden -m-6 rounded-[32px]">
                    <ProfileSettingsPage initialSettings={settings} onSave={handleSaveSettings} user={user} db={db} onNavigate={(view) => safeNavigate('/' + view.toLowerCase())} />
                  </div>
                } />

                <Route path="/activity" element={
                  <AllActivityPage db={db} user={user} onBack={() => safeNavigate('/dashboard')} />
                } />

                <Route path="/episodes" element={
                  <EpisodesHubPage episodes={scheduledLeads} onSelectEpisode={(ep) => safeNavigate(`/episodes/${ep.id}`)} onDeleteEpisode={handleDeleteEpisode} db={db} user={user} />
                } />

                <Route path="/episodes/:id" element={
                  <EpisodeRoute
                    scheduledLeads={scheduledLeads}
                    leads={leads}
                    setLeads={setLeads}
                    user={user}
                    db={db}
                    settings={settings}
                    setSelectedLead={setSelectedLead}
                  />
                } />

                {/* Guest Portal Route - Handles /guest/* */}
                <Route path="/guest/*" element={<GuestPortalCompact />} />

                {/* Waiting List Route - Public page for waitlist signup */}
                <Route path="/waitlist" element={<WaitingListPage />} />

                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </main>
          </div>

          {/* Guest Drawer */}
          <GuestDrawer
            lead={selectedLead}
            isOpen={!!selectedLead}
            onClose={() => {
              setSelectedLead(null);
              setDiscoveryMaterials(null); // Clear materials on close
            }}
            onUpdateLeadStatus={handleUpdateLeadStatus}
            onSendEmail={handleSendEmail}
            user={user}
            db={db}
            settings={settings}
            initialMaterials={discoveryMaterials}
            templates={templates}
          />
          <AddLeadModal isOpen={isAddLeadModalOpen} onClose={() => startTransition(() => setAddLeadModalOpen(false))} onAddLead={handleAddLead} existingLeads={leads || []} user={user} />
        </div>
      </Suspense>
    </ThemeProvider >
  );
}